import React, { useEffect, useState, useMemo } from 'react';
import {
  Wallet, TrendingUp, Target, BarChart3, Clock, Trophy, XCircle, Minus,
  ChevronDown, Trash2, CheckCircle, Loader2, Plus, RotateCcw, Pencil, X, RefreshCw,
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  BarChart, Bar, Cell, AreaChart, Area,
} from 'recharts';
import { apiGet, apiPost, apiDelete, apiPut } from '../data/api';

const mono = { fontFamily: 'JetBrains Mono, monospace' };
const heading = { fontFamily: 'Outfit, sans-serif' };
const card = { background: '#18181B', border: '1px solid #27272A' };

const MARKET_LABELS = { match_winner: 'MW', over_under: 'O/U', handicap: 'HC' };
const STATUS_CONFIG = {
  pending: { label: 'Pending', icon: Clock, cls: 'bg-amber-500/15 text-amber-400' },
  won: { label: 'Vinta', icon: Trophy, cls: 'bg-emerald-500/15 text-emerald-400' },
  lost: { label: 'Persa', icon: XCircle, cls: 'bg-rose-500/15 text-rose-400' },
  void: { label: 'Void', icon: Minus, cls: 'bg-zinc-500/15 text-zinc-400' },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold rounded ${cfg.cls}`} data-testid={`status-badge-${status}`}>
      <Icon size={10} />
      {cfg.label}
    </span>
  );
}

function KpiCard({ label, value, sub, icon: Icon, color = 'zinc', testId }) {
  const colorMap = {
    emerald: 'text-emerald-400',
    rose: 'text-rose-400',
    amber: 'text-amber-400',
    zinc: 'text-zinc-200',
    blue: 'text-blue-400',
  };
  return (
    <div className="rounded-sm p-4" style={card} data-testid={testId}>
      <div className="flex items-center gap-2 mb-2">
        {Icon && <Icon size={14} className="text-zinc-500" />}
        <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-medium">{label}</span>
      </div>
      <div className={`text-xl font-bold ${colorMap[color]}`} style={mono}>{value}</div>
      {sub && <div className="text-[10px] text-zinc-600 mt-1">{sub}</div>}
    </div>
  );
}

function ChartTooltipContent({ active, payload, label, prefix = '', suffix = '' }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-sm p-2 text-xs" style={{ background: '#27272A', border: '1px solid #3F3F46' }}>
      <div className="text-zinc-400 mb-1">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span style={{ color: p.color, width: 8, height: 8, borderRadius: 2, display: 'inline-block', background: p.color }} />
          <span className="text-zinc-300" style={mono}>{prefix}{typeof p.value === 'number' ? p.value.toFixed(2) : p.value}{suffix}</span>
        </div>
      ))}
    </div>
  );
}

/* ─── Bets Table ─── */
function BetsTable({ bets, onSettle, onDelete, onReSettle }) {
  const [editingId, setEditingId] = React.useState(null);
  if (!bets.length) return <div className="text-center py-12 text-zinc-500 text-sm">Nessuna giocata registrata</div>;

  return (
    <div className="rounded-sm overflow-x-auto" style={card} data-testid="bets-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-[10px] text-zinc-500 uppercase tracking-wider" style={{ borderBottom: '1px solid #27272A' }}>
            <th className="text-left px-4 py-3 font-medium">Data</th>
            <th className="text-left px-3 py-3 font-medium">Selezione</th>
            <th className="text-center px-3 py-3 font-medium">Mercato</th>
            <th className="text-center px-3 py-3 font-medium">Quota</th>
            <th className="text-center px-3 py-3 font-medium">Stake</th>
            <th className="text-center px-3 py-3 font-medium">Stato</th>
            <th className="text-right px-3 py-3 font-medium">P/L</th>
            <th className="text-center px-3 py-3 font-medium">Azioni</th>
          </tr>
        </thead>
        <tbody>
          {bets.map(b => {
            const mktLabel = MARKET_LABELS[b.market_type] || b.market_type;
            const isPending = b.result_status === 'pending';
            const isSettled = ['won', 'lost', 'void'].includes(b.result_status);
            const isEditing = editingId === b.id;
            return (
              <tr key={b.id} className="border-t border-zinc-800/50 hover:bg-zinc-800/20 transition-colors" data-testid={`bet-row-${b.id}`}>
                <td className="px-4 py-3 text-xs text-zinc-400 whitespace-nowrap">
                  {b.placed_at ? new Date(b.placed_at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}
                </td>
                <td className="px-3 py-3">
                  <div className="text-zinc-200 text-xs">{b.selection_name}</div>
                  {b.bookmaker_name && <div className="text-[10px] text-zinc-600">{b.bookmaker_name}</div>}
                </td>
                <td className="px-3 py-3 text-center">
                  <span className="px-1.5 py-0.5 text-[9px] font-bold rounded bg-zinc-800 text-zinc-400">{mktLabel}</span>
                </td>
                <td className="px-3 py-3 text-center font-mono text-zinc-300 text-xs">{b.odds_taken?.toFixed(2)}</td>
                <td className="px-3 py-3 text-center font-mono text-zinc-300 text-xs">{b.stake_amount?.toFixed(2)}u</td>
                <td className="px-3 py-3 text-center"><StatusBadge status={b.result_status} /></td>
                <td className="px-3 py-3 text-right">
                  {b.profit_loss != null ? (
                    <span className={`font-mono text-xs font-bold ${b.profit_loss >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                      {b.profit_loss >= 0 ? '+' : ''}{b.profit_loss.toFixed(2)}u
                    </span>
                  ) : <span className="text-zinc-600 text-xs">—</span>}
                </td>
                <td className="px-3 py-3 text-center">
                  {isPending && (
                    <div className="flex items-center justify-center gap-1">
                      <button onClick={() => onSettle(b.id, 'won')} title="Vinta"
                        className="p-1 rounded hover:bg-emerald-500/20 text-zinc-500 hover:text-emerald-400 transition-colors" data-testid={`settle-won-${b.id}`}>
                        <Trophy size={13} />
                      </button>
                      <button onClick={() => onSettle(b.id, 'lost')} title="Persa"
                        className="p-1 rounded hover:bg-rose-500/20 text-zinc-500 hover:text-rose-400 transition-colors" data-testid={`settle-lost-${b.id}`}>
                        <XCircle size={13} />
                      </button>
                      <button onClick={() => onSettle(b.id, 'void')} title="Void"
                        className="p-1 rounded hover:bg-zinc-700 text-zinc-600 hover:text-zinc-400 transition-colors" data-testid={`settle-void-${b.id}`}>
                        <Minus size={13} />
                      </button>
                      <button onClick={() => onDelete(b.id)} title="Elimina"
                        className="p-1 rounded hover:bg-rose-500/10 text-zinc-600 hover:text-rose-400 transition-colors" data-testid={`delete-bet-${b.id}`}>
                        <Trash2 size={12} />
                      </button>
                    </div>
                  )}
                  {isSettled && !isEditing && (
                    <div className="flex items-center justify-center gap-1">
                      <button onClick={() => setEditingId(b.id)} title="Modifica risultato"
                        className="p-1 rounded hover:bg-blue-500/20 text-zinc-600 hover:text-blue-400 transition-colors" data-testid={`edit-result-${b.id}`}>
                        <Pencil size={12} />
                      </button>
                      {b.auto_settled && <span className="text-[9px] text-zinc-600 ml-1">auto</span>}
                    </div>
                  )}
                  {isSettled && isEditing && (
                    <div className="flex items-center justify-center gap-1">
                      {b.result_status !== 'won' && (
                        <button onClick={() => { onReSettle(b.id, 'won'); setEditingId(null); }} title="Vinta"
                          className="p-1 rounded hover:bg-emerald-500/20 text-zinc-500 hover:text-emerald-400 transition-colors" data-testid={`resettle-won-${b.id}`}>
                          <Trophy size={13} />
                        </button>
                      )}
                      {b.result_status !== 'lost' && (
                        <button onClick={() => { onReSettle(b.id, 'lost'); setEditingId(null); }} title="Persa"
                          className="p-1 rounded hover:bg-rose-500/20 text-zinc-500 hover:text-rose-400 transition-colors" data-testid={`resettle-lost-${b.id}`}>
                          <XCircle size={13} />
                        </button>
                      )}
                      {b.result_status !== 'void' && (
                        <button onClick={() => { onReSettle(b.id, 'void'); setEditingId(null); }} title="Void"
                          className="p-1 rounded hover:bg-zinc-700 text-zinc-600 hover:text-zinc-400 transition-colors" data-testid={`resettle-void-${b.id}`}>
                          <Minus size={13} />
                        </button>
                      )}
                      <button onClick={() => setEditingId(null)} title="Annulla"
                        className="p-1 rounded hover:bg-zinc-700 text-zinc-600 hover:text-zinc-400 transition-colors" data-testid={`cancel-edit-${b.id}`}>
                        <X size={12} />
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ─── Main Dashboard ─── */
export default function BetTracking() {
  const [stats, setStats] = useState(null);
  const [bets, setBets] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState('');
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [panelMode, setPanelMode] = useState(null); // 'create' | 'edit' | null
  const [formData, setFormData] = useState({ name: '', starting_bankroll: '100', stake_mode_default: 'adaptive_kelly', bet_step: '0.50', min_bet_amount: '1', max_bet_amount: '50' });

  const STAKE_MODES = [
    { value: 'adaptive_kelly', label: 'Adaptive Kelly' },
    { value: 'full_kelly', label: 'Full Kelly' },
    { value: 'half_kelly', label: 'Half Kelly' },
    { value: 'quarter_kelly', label: 'Quarter Kelly' },
    { value: 'fixed', label: 'Fixed' },
  ];

  const loadData = async (profileId) => {
    setLoading(true);
    try {
      // Always load profiles first
      const p = await apiGet('/tt/bankroll-profiles');
      setProfiles(p);

      // Determine which profile to use
      let pId = profileId || '';
      if (!pId && p.length) {
        const def = p.find(pr => pr.is_default) || p[0];
        pId = def?.id || '';
        setSelectedProfile(pId);
      }

      // Only load data if we have a profile
      if (pId) {
        const [s, b] = await Promise.all([
          apiGet(`/tt/tracking-stats?profile_id=${pId}`),
          apiGet(`/tt/placed-bets?profile_id=${pId}`),
        ]);
        setStats(s);
        setBets(b);
      } else {
        setStats(null);
        setBets([]);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadData(selectedProfile); }, [selectedProfile]);

  const handleSettle = async (betId, resultStatus) => {
    await apiPost(`/tt/placed-bets/${betId}/settle`, { result_status: resultStatus });
    loadData(selectedProfile);
  };

  const handleReSettle = async (betId, resultStatus) => {
    await apiPost(`/tt/placed-bets/${betId}/re-settle`, { result_status: resultStatus });
    loadData(selectedProfile);
  };

  const handleDelete = async (betId) => {
    try {
      await apiDelete(`/tt/placed-bets/${betId}`);
      loadData(selectedProfile);
    } catch (e) { console.error(e); }
  };

  const handleResetBankroll = async () => {
    if (!selectedProfile) return;
    await apiPost(`/tt/bankroll-profiles/${selectedProfile}/reset`, {});
    loadData(selectedProfile);
  };

  const handleRecalcBankroll = async () => {
    if (!selectedProfile) return;
    await apiPost(`/tt/bankroll-profiles/${selectedProfile}/recalculate`, {});
    loadData(selectedProfile);
  };

  const handleDeleteProfile = async () => {
    if (!selectedProfile) return;
    try {
      await apiDelete(`/tt/bankroll-profiles/${selectedProfile}`);
      setSelectedProfile('');
      loadData('');
    } catch (e) { console.error(e); }
  };

  const openCreatePanel = () => {
    setFormData({ name: '', starting_bankroll: '100', stake_mode_default: 'adaptive_kelly', bet_step: '0.50', min_bet_amount: '1', max_bet_amount: '50' });
    setPanelMode('create');
  };

  const openEditPanel = () => {
    if (!currentProfile) return;
    setFormData({
      name: currentProfile.name,
      starting_bankroll: currentProfile.starting_bankroll.toString(),
      stake_mode_default: currentProfile.stake_mode_default,
      bet_step: currentProfile.bet_step.toString(),
      min_bet_amount: currentProfile.min_bet_amount.toString(),
      max_bet_amount: currentProfile.max_bet_amount.toString(),
    });
    setPanelMode('edit');
  };

  const handleSaveProfile = async () => {
    const payload = {
      name: formData.name,
      starting_bankroll: parseFloat(formData.starting_bankroll),
      stake_mode_default: formData.stake_mode_default,
      bet_step: parseFloat(formData.bet_step),
      min_bet_amount: parseFloat(formData.min_bet_amount),
      max_bet_amount: parseFloat(formData.max_bet_amount),
    };
    if (panelMode === 'create') {
      const p = await apiPost('/tt/bankroll-profiles', payload);
      setSelectedProfile(p.id);
    } else {
      await apiPut(`/tt/bankroll-profiles/${selectedProfile}`, payload);
    }
    setPanelMode(null);
    loadData(selectedProfile);
  };

  const filteredBets = useMemo(() => {
    if (statusFilter === 'all') return bets;
    return bets.filter(b => b.result_status === statusFilter);
  }, [bets, statusFilter]);

  const currentProfile = profiles.find(p => p.id === selectedProfile);

  if (loading && !stats) {
    return (
      <div className="flex justify-center py-20" data-testid="bet-tracking-loading">
        <Loader2 className="animate-spin text-zinc-500" size={24} />
      </div>
    );
  }

  const s = stats || {};
  const brHistory = s.bankroll_history || [];
  const profitHistory = s.profit_history || [];
  const evVsReal = s.ev_vs_real_history || [];
  const stakeDist = s.stake_distribution || [];
  const byConf = s.by_confidence || [];
  const byEv = s.by_ev_range || [];
  const byMarket = s.by_market || [];

  // Format dates for charts
  const fmtDate = (d) => {
    if (!d) return '';
    const dt = new Date(d);
    return `${dt.getDate()}/${dt.getMonth() + 1}`;
  };

  const bankrollChartData = brHistory.map(h => ({ ...h, label: fmtDate(h.date) }));
  const profitChartData = profitHistory.map(h => ({ ...h, label: fmtDate(h.date) }));
  const evRealChartData = evVsReal.map(h => ({ ...h, label: fmtDate(h.date) }));

  // Starting bankroll line for chart
  const startingBR = currentProfile?.starting_bankroll || 100;

  return (
    <div data-testid="bet-tracking-page" className="space-y-5 max-w-[1200px]">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-zinc-50" style={heading}>Bet Tracking</h1>
        <div className="flex items-center gap-2">
          <select
            data-testid="profile-selector"
            value={selectedProfile}
            onChange={e => { setSelectedProfile(e.target.value); setPanelMode(null); }}
            className="bg-zinc-900 border border-zinc-700 rounded-sm px-3 py-1.5 text-xs text-zinc-300 focus:outline-none focus:border-emerald-500/50"
          >
            {profiles.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          <button onClick={openCreatePanel} data-testid="create-profile-btn" title="Nuovo profilo"
            className="p-1.5 rounded-sm text-zinc-500 hover:text-emerald-400 hover:bg-emerald-500/10 transition-colors">
            <Plus size={15} />
          </button>
          <button onClick={openEditPanel} data-testid="edit-profile-btn" title="Modifica profilo"
            className="p-1.5 rounded-sm text-zinc-500 hover:text-blue-400 hover:bg-blue-500/10 transition-colors">
            <Pencil size={14} />
          </button>
          <button onClick={handleResetBankroll} data-testid="reset-bankroll-btn" title="Reset bankroll"
            className="p-1.5 rounded-sm text-zinc-500 hover:text-amber-400 hover:bg-amber-500/10 transition-colors">
            <RotateCcw size={14} />
          </button>
          <button onClick={handleRecalcBankroll} data-testid="recalc-bankroll-btn" title="Ricalcola bankroll"
            className="p-1.5 rounded-sm text-zinc-500 hover:text-emerald-400 hover:bg-emerald-500/10 transition-colors">
            <RefreshCw size={14} />
          </button>
          {currentProfile && !currentProfile.is_default && (
            <button onClick={handleDeleteProfile} data-testid="delete-profile-btn" title="Elimina profilo"
              className="p-1.5 rounded-sm text-zinc-500 hover:text-rose-400 hover:bg-rose-500/10 transition-colors">
              <Trash2 size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Profile Create/Edit Panel */}
      {panelMode && (
        <div className="rounded-sm p-4" style={card} data-testid="profile-panel">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-zinc-200" style={heading}>
              {panelMode === 'create' ? 'Nuovo Profilo' : 'Modifica Profilo'}
            </h3>
            <button onClick={() => setPanelMode(null)} className="text-zinc-500 hover:text-zinc-300" data-testid="close-panel-btn">
              <X size={16} />
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Nome</label>
              <input data-testid="profile-name-input" type="text" value={formData.name}
                onChange={e => setFormData(f => ({ ...f, name: e.target.value }))}
                className="w-full bg-zinc-900 border border-zinc-700 rounded-sm px-2.5 py-1.5 text-xs text-zinc-200 focus:outline-none focus:border-emerald-500/50"
                placeholder="es. Aggressivo" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Bankroll</label>
              <input data-testid="profile-bankroll-input" type="number" step="10" value={formData.starting_bankroll}
                onChange={e => setFormData(f => ({ ...f, starting_bankroll: e.target.value }))}
                className="w-full bg-zinc-900 border border-zinc-700 rounded-sm px-2.5 py-1.5 text-xs text-zinc-200 focus:outline-none focus:border-emerald-500/50" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Stake Mode</label>
              <select data-testid="profile-stake-mode" value={formData.stake_mode_default}
                onChange={e => setFormData(f => ({ ...f, stake_mode_default: e.target.value }))}
                className="w-full bg-zinc-900 border border-zinc-700 rounded-sm px-2.5 py-1.5 text-xs text-zinc-200 focus:outline-none focus:border-emerald-500/50">
                {STAKE_MODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Step</label>
              <input data-testid="profile-step-input" type="number" step="0.25" value={formData.bet_step}
                onChange={e => setFormData(f => ({ ...f, bet_step: e.target.value }))}
                className="w-full bg-zinc-900 border border-zinc-700 rounded-sm px-2.5 py-1.5 text-xs text-zinc-200 focus:outline-none focus:border-emerald-500/50" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Min / Max</label>
              <div className="flex gap-1.5">
                <input data-testid="profile-min-input" type="number" step="0.5" value={formData.min_bet_amount}
                  onChange={e => setFormData(f => ({ ...f, min_bet_amount: e.target.value }))}
                  className="w-full bg-zinc-900 border border-zinc-700 rounded-sm px-2 py-1.5 text-xs text-zinc-200 focus:outline-none focus:border-emerald-500/50" />
                <input data-testid="profile-max-input" type="number" step="1" value={formData.max_bet_amount}
                  onChange={e => setFormData(f => ({ ...f, max_bet_amount: e.target.value }))}
                  className="w-full bg-zinc-900 border border-zinc-700 rounded-sm px-2 py-1.5 text-xs text-zinc-200 focus:outline-none focus:border-emerald-500/50" />
              </div>
            </div>
            <div className="flex items-end">
              <button onClick={handleSaveProfile} data-testid="save-profile-btn"
                disabled={!formData.name}
                className={`w-full py-1.5 rounded-sm text-xs font-semibold transition-colors flex items-center justify-center gap-1.5 ${
                  formData.name ? 'bg-emerald-500 text-black hover:bg-emerald-400' : 'bg-zinc-800 text-zinc-600 cursor-not-allowed'
                }`}>
                <CheckCircle size={13} />
                {panelMode === 'create' ? 'Crea' : 'Salva'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* KPI Cards Row 1 */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KpiCard
          label="Bankroll Iniziale"
          value={`${startingBR.toFixed(2)}€`}
          icon={Wallet}
          testId="kpi-starting-br"
        />
        <KpiCard
          label="Bankroll Attuale"
          value={`${(currentProfile?.current_bankroll || 0).toFixed(2)}€`}
          icon={Wallet}
          color={(currentProfile?.current_bankroll || 0) >= startingBR ? 'emerald' : 'rose'}
          testId="kpi-current-br"
        />
        <KpiCard
          label="Profit / Loss"
          value={`${s.total_profit >= 0 ? '+' : ''}${(s.total_profit || 0).toFixed(2)}€`}
          color={s.total_profit >= 0 ? 'emerald' : 'rose'}
          icon={TrendingUp}
          testId="kpi-total-pl"
        />
        <KpiCard
          label="ROI"
          value={`${s.roi_pct >= 0 ? '+' : ''}${(s.roi_pct || 0).toFixed(1)}%`}
          color={s.roi_pct >= 0 ? 'emerald' : 'rose'}
          icon={Target}
          testId="kpi-roi"
        />
        <KpiCard
          label="Yield"
          value={`${s.yield_pct >= 0 ? '+' : ''}${(s.yield_pct || 0).toFixed(2)}€`}
          color={s.yield_pct >= 0 ? 'emerald' : 'rose'}
          icon={BarChart3}
          testId="kpi-yield"
        />
      </div>

      {/* KPI Cards Row 2 */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KpiCard label="Giocate Totali" value={s.total_bets || 0} icon={BarChart3} testId="kpi-total-bets" />
        <KpiCard
          label="Win Rate"
          value={`${(s.win_rate || 0).toFixed(1)}%`}
          color={s.win_rate >= 50 ? 'emerald' : s.win_rate > 0 ? 'amber' : 'zinc'}
          icon={Trophy}
          testId="kpi-win-rate"
        />
        <KpiCard label="Pending" value={s.pending || 0} color="amber" icon={Clock} testId="kpi-pending" />
        <KpiCard
          label="Profitto 7gg"
          value={`${s.profit_7d >= 0 ? '+' : ''}${(s.profit_7d || 0).toFixed(2)}€`}
          color={s.profit_7d >= 0 ? 'emerald' : 'rose'}
          testId="kpi-profit-7d"
        />
        <KpiCard
          label="Profitto 30gg"
          value={`${s.profit_30d >= 0 ? '+' : ''}${(s.profit_30d || 0).toFixed(2)}€`}
          color={s.profit_30d >= 0 ? 'emerald' : 'rose'}
          testId="kpi-profit-30d"
        />
      </div>

      {/* Main Chart: Bankroll Over Time */}
      <div className="rounded-sm p-5" style={card} data-testid="chart-bankroll">
        <h2 className="text-sm font-semibold text-zinc-300 mb-4" style={heading}>Andamento Bankroll</h2>
        {bankrollChartData.length > 1 ? (
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={[{ label: 'Start', bankroll: startingBR }, ...bankrollChartData]}>
              <defs>
                <linearGradient id="brGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10B981" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
              <XAxis dataKey="label" tick={{ fill: '#71717A', fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#71717A', fontSize: 10 }} axisLine={false} tickLine={false} domain={['auto', 'auto']} />
              <Tooltip content={<ChartTooltipContent suffix="€" />} />
              <Area type="monotone" dataKey="bankroll" stroke="#10B981" strokeWidth={2} fill="url(#brGrad)" dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-[280px] flex items-center justify-center text-zinc-600 text-sm">
            Dati insufficienti — chiudi almeno 2 giocate per vedere il grafico
          </div>
        )}
      </div>

      {/* Secondary Charts Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Profit Cumulato */}
        <div className="rounded-sm p-5" style={card} data-testid="chart-profit">
          <h2 className="text-sm font-semibold text-zinc-300 mb-4" style={heading}>Profit Cumulato</h2>
          {profitChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={profitChartData}>
                <defs>
                  <linearGradient id="profGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.12} />
                    <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                <XAxis dataKey="label" tick={{ fill: '#71717A', fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: '#71717A', fontSize: 10 }} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTooltipContent prefix="" suffix="€" />} />
                <Area type="monotone" dataKey="profit" stroke="#10B981" strokeWidth={2} fill="url(#profGrad)" dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[200px] flex items-center justify-center text-zinc-600 text-xs">Nessun dato</div>
          )}
        </div>

        {/* EV vs Real */}
        <div className="rounded-sm p-5" style={card} data-testid="chart-ev-vs-real">
          <h2 className="text-sm font-semibold text-zinc-300 mb-4" style={heading}>EV Teorico vs Profitto Reale</h2>
          {evRealChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={evRealChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                <XAxis dataKey="label" tick={{ fill: '#71717A', fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: '#71717A', fontSize: 10 }} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTooltipContent suffix="€" />} />
                <Line type="monotone" dataKey="ev_cumulative" stroke="#3B82F6" strokeWidth={2} dot={false} name="EV Teorico" />
                <Line type="monotone" dataKey="real_cumulative" stroke="#10B981" strokeWidth={2} dot={false} name="Profitto Reale" />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[200px] flex items-center justify-center text-zinc-600 text-xs">Nessun dato</div>
          )}
        </div>
      </div>

      {/* Third Row Charts */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Stake Distribution */}
        <div className="rounded-sm p-5" style={card} data-testid="chart-stake-dist">
          <h2 className="text-sm font-semibold text-zinc-300 mb-4" style={heading}>Distribuzione Stake</h2>
          {stakeDist.some(d => d.count > 0) ? (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={stakeDist}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                <XAxis dataKey="range" tick={{ fill: '#71717A', fontSize: 9 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: '#71717A', fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip content={<ChartTooltipContent />} />
                <Bar dataKey="count" radius={[2, 2, 0, 0]}>
                  {stakeDist.map((_, i) => <Cell key={i} fill={i % 2 === 0 ? '#10B981' : '#059669'} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[180px] flex items-center justify-center text-zinc-600 text-xs">Nessun dato</div>
          )}
        </div>

        {/* By Confidence */}
        <div className="rounded-sm p-5" style={card} data-testid="chart-by-confidence">
          <h2 className="text-sm font-semibold text-zinc-300 mb-4" style={heading}>Per Confidence</h2>
          {byConf.length > 0 ? (
            <div className="space-y-2">
              {byConf.map((c, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  <span className="text-zinc-400 w-16" style={mono}>{c.range}</span>
                  <span className="text-zinc-500 w-8 text-center">{c.count}</span>
                  <span className="text-zinc-500 w-10 text-center">{c.wins}W</span>
                  <span className={`font-bold flex-1 text-right ${c.profit >= 0 ? 'text-emerald-400' : 'text-rose-400'}`} style={mono}>
                    {c.profit >= 0 ? '+' : ''}{c.profit.toFixed(2)}€
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="h-[180px] flex items-center justify-center text-zinc-600 text-xs">Nessun dato</div>
          )}
        </div>

        {/* By EV Range */}
        <div className="rounded-sm p-5" style={card} data-testid="chart-by-ev">
          <h2 className="text-sm font-semibold text-zinc-300 mb-4" style={heading}>Per EV Range</h2>
          {byEv.length > 0 ? (
            <div className="space-y-2">
              {byEv.map((c, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  <span className="text-zinc-400 w-16" style={mono}>{c.range}</span>
                  <span className="text-zinc-500 w-8 text-center">{c.count}</span>
                  <span className="text-zinc-500 w-10 text-center">{c.wins}W</span>
                  <span className={`font-bold flex-1 text-right ${c.profit >= 0 ? 'text-emerald-400' : 'text-rose-400'}`} style={mono}>
                    {c.profit >= 0 ? '+' : ''}{c.profit.toFixed(2)}€
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="h-[180px] flex items-center justify-center text-zinc-600 text-xs">Nessun dato</div>
          )}
        </div>
      </div>

      {/* By Market */}
      {byMarket.length > 0 && (
        <div className="rounded-sm p-5" style={card} data-testid="chart-by-market">
          <h2 className="text-sm font-semibold text-zinc-300 mb-4" style={heading}>Performance per Mercato</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {byMarket.map((m, i) => (
              <div key={i} className="rounded-sm p-3" style={{ background: '#09090B', border: '1px solid #27272A' }}>
                <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">{MARKET_LABELS[m.market] || m.market}</div>
                <div className="grid grid-cols-2 gap-y-1 text-xs">
                  <span className="text-zinc-500">Giocate</span>
                  <span className="text-zinc-300 text-right" style={mono}>{m.count}</span>
                  <span className="text-zinc-500">W / L</span>
                  <span className="text-zinc-300 text-right" style={mono}>{m.wins} / {m.losses}</span>
                  <span className="text-zinc-500">Profit</span>
                  <span className={`text-right font-bold ${m.profit >= 0 ? 'text-emerald-400' : 'text-rose-400'}`} style={mono}>
                    {m.profit >= 0 ? '+' : ''}{m.profit.toFixed(2)}€
                  </span>
                  <span className="text-zinc-500">ROI</span>
                  <span className={`text-right font-bold ${m.roi >= 0 ? 'text-emerald-400' : 'text-rose-400'}`} style={mono}>
                    {m.roi >= 0 ? '+' : ''}{m.roi.toFixed(1)}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bets Table */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-zinc-300" style={heading}>Storico Giocate</h2>
          <div className="flex gap-1.5">
            {['all', 'pending', 'won', 'lost', 'void'].map(f => (
              <button
                key={f}
                data-testid={`bet-filter-${f}`}
                onClick={() => setStatusFilter(f)}
                className={`px-2.5 py-1 text-[10px] rounded-sm transition-colors ${
                  statusFilter === f
                    ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                    : 'bg-zinc-800/50 text-zinc-500 border border-zinc-700/50 hover:text-zinc-300'
                }`}
              >
                {f === 'all' ? 'Tutte' : STATUS_CONFIG[f]?.label || f}
              </button>
            ))}
          </div>
        </div>
        <BetsTable bets={filteredBets} onSettle={handleSettle} onDelete={handleDelete} onReSettle={handleReSettle} />
      </div>
    </div>
  );
}
